import React, { useState } from 'react';
import {
    Typography,
    Box,
    TextField,
    IconButton,
    List,
    ListItem,
    ListItemText,
    InputBase,
    Button,
    CircularProgress,
    Alert,
} from '@mui/material';
import AddIcon from '@mui/icons-material/Add';
import NoteAltRoundedIcon from '@mui/icons-material/NoteAltRounded';
import EditIcon from '@mui/icons-material/Edit';
import DeleteIcon from '@mui/icons-material/Delete';
import DoneIcon from '@mui/icons-material/Done';
import CloseIcon from '@mui/icons-material/Close';
import WidgetCard from './WidgetCard';
import { Link } from 'react-router-dom';
import { useCommentData, useCommentMutations } from '../../hooks/useCommentData';

const TodaysNotesWidget: React.FC = () => {
    const [newNote, setNewNote] = useState('');
    const [editNoteId, setEditNoteId] = useState<string | null>(null);
    const [editedContent, setEditedContent] = useState('');

    // Use optimized hooks for data management
    const { notes, isLoading, error, noteToComment } = useCommentData();
    const { createComment, updateComment, deleteComment, isCreating, isUpdating, isDeleting } = useCommentMutations();

    // Derived state for UI
    const loading = isLoading;
    const saving = isCreating || isUpdating || isDeleting;
    const errorMessage = error ? (error instanceof Error ? error.message : String(error) || 'Wystąpił błąd podczas ładowania notatek') : null;

    const handleAddNote = () => {
        if (!newNote.trim()) return;

        const noteData = noteToComment({ content: newNote.trim() });
        createComment({ requestBody: noteData });
        setNewNote('');
    };

    const handleDeleteNote = (id: string) => {
        deleteComment({ id: parseInt(id) });
    };

    const handleStartEdit = (id: string, content: string) => {
        setEditNoteId(id);
        setEditedContent(content);
    };

    const handleCancelEdit = () => {
        setEditNoteId(null);
        setEditedContent('');
    };

    const handleSaveEdit = () => {
        if (!editedContent.trim() || !editNoteId) return;

        const noteData = noteToComment({ 
            id: editNoteId, 
            content: editedContent.trim() 
        });

        updateComment({
            id: parseInt(editNoteId),
            requestBody: noteData
        });

        setEditNoteId(null);
        setEditedContent('');
    };

    const todayDate = new Date().toISOString().split('T')[0];
    const todayNotes = notes.filter(note => note.createdAt.startsWith(todayDate));

    return (
        <WidgetCard>
            <Box
                mb={1.5}
                display="flex"
                justifyContent="space-between"
                alignItems="center"
                pb={1}
                borderBottom="1px solid"
                borderColor="divider"
            >
                <Box display="flex" alignItems="center">
                    <Box
                        sx={{
                            backgroundColor: '#fffde7',
                            borderRadius: '50%',
                            width: 28,
                            height: 28,
                            display: 'flex',
                            alignItems: 'center',
                            justifyContent: 'center',
                            mr: 1,
                        }}
                    >
                        <NoteAltRoundedIcon sx={{ color: '#fbc02d', fontSize: 18 }} />
                    </Box>
                    <Typography variant="subtitle1" fontWeight={600} fontSize="0.95rem">
                        Notatki
                    </Typography>
                </Box>

                <Button
                    component={Link}
                    to="/notes"
                    size="small"
                    variant="outlined"
                    sx={{
                        borderRadius: '999px',
                        textTransform: 'none',
                        fontSize: '0.75rem',
                        px: 1.5,
                        py: 0.25,
                        height: '24px',
                        minWidth: 'auto',
                        lineHeight: 1,
                    }}
                >
                    Zobacz wszystkie
                </Button>
            </Box>

            {/* Error Alert */}
            {errorMessage && (
                <Alert severity="error" sx={{ mb: 2, fontSize: '0.75rem' }}>
                    {errorMessage}
                </Alert>
            )}

            <Box display="flex" mb={2}>
                <TextField
                    variant="outlined"
                    size="small"
                    placeholder="Dodaj notatkę..."
                    value={newNote}
                    onChange={(e) => setNewNote(e.target.value)}
                    fullWidth
                    disabled={saving}
                />
                <IconButton 
                    color="primary" 
                    onClick={handleAddNote} 
                    sx={{ ml: 1 }}
                    disabled={saving || !newNote.trim()}
                >
                    {saving ? <CircularProgress size={20} /> : <AddIcon />}
                </IconButton>
            </Box>

            {/* Loading State */}
            {loading ? (
                <Box sx={{ display: 'flex', justifyContent: 'center', py: 2 }}>
                    <CircularProgress size={24} />
                </Box>
            ) : (
                <List disablePadding sx={{ mt: 1 }}>
                    {todayNotes.length > 0 ? todayNotes.map((note) => (
                        <ListItem
                            key={note.id}
                            disableGutters
                            sx={{
                                px: 0,
                                py: 0.75,
                                display: 'flex',
                                justifyContent: 'space-between',
                                alignItems: 'flex-start',
                            }}
                        >
                            {editNoteId === note.id ? (
                                <Box sx={{ flexGrow: 1, display: 'flex', gap: 1 }}>
                                    <InputBase
                                        fullWidth
                                        value={editedContent}
                                        onChange={(e) => setEditedContent(e.target.value)}
                                        sx={{
                                            fontSize: '0.875rem',
                                            borderBottom: '1px solid #ccc',
                                            pb: 0.25,
                                        }}
                                        disabled={saving}
                                    />
                                    <IconButton size="small" onClick={handleSaveEdit} disabled={saving}>
                                        {saving ? <CircularProgress size={16} /> : <DoneIcon fontSize="small" />}
                                    </IconButton>
                                    <IconButton size="small" onClick={handleCancelEdit} disabled={saving}>
                                        <CloseIcon fontSize="small" />
                                    </IconButton>
                                </Box>
                            ) : (
                                <>
                                    <ListItemText
                                        primary={
                                            <Link
                                                to={`/notes#${note.id}`}
                                                style={{ textDecoration: 'none', color: 'inherit' }}
                                            >
                                                <Typography
                                                    fontSize="0.875rem"
                                                    sx={{
                                                        display: '-webkit-box',
                                                        WebkitBoxOrient: 'vertical',
                                                        WebkitLineClamp: 3,
                                                        overflow: 'hidden',
                                                        textOverflow: 'ellipsis',
                                                        whiteSpace: 'pre-wrap',
                                                        wordBreak: 'break-word',
                                                        cursor: 'pointer',
                                                        '&:hover': {
                                                            textDecoration: 'underline',
                                                        },
                                                    }}
                                                >
                                                    {note.content}
                                                </Typography>
                                            </Link>
                                        }
                                    />
                                    <Box sx={{ flexShrink: 0, ml: 1 }}>
                                        <IconButton size="small" onClick={() => handleStartEdit(note.id, note.content)}>
                                            <EditIcon fontSize="small" />
                                        </IconButton>
                                        <IconButton size="small" onClick={() => handleDeleteNote(note.id)}>
                                            <DeleteIcon fontSize="small" />
                                        </IconButton>
                                    </Box>
                                </>
                            )}
                        </ListItem>
                    )) : (
                        <ListItem disableGutters sx={{ px: 0, py: 0.75 }}>
                            <ListItemText
                                primary={
                                    <Typography fontSize="0.875rem" color="text.secondary">
                                        Brak notatek z dzisiaj.
                                    </Typography>
                                }
                            />
                        </ListItem>
                    )}
                </List>
            )}
        </WidgetCard>
    );
};

export default TodaysNotesWidget;
